// actions — the one door every Field write goes through.
//
//   act(op) → { sent: true, result } | { sent: false, queued }
//
// Online with an empty queue: send the single op to /api/field/sync now (it
// carries its own key, so a lost reply can be resent without double-booking
// anyone). No answer → enqueue, exactly as if we had been offline. The server
// saying no → thrown as an HttpError so the screen shows the sentence.
//
// Offline, OR online with ops still waiting: enqueue behind them. Order is
// the invariant — a clock_out must not overtake the clock_in it closes — so
// the moment anything is queued, everything queues until the queue drains.
//
// clockIn / clockOut also keep the optimistic `now` (offline/optimisticNow.js)
// so Today shows a running clock before the server has heard about it.
//
// THREE writes deliberately do not queue, and each says so where it is
// defined: `claimJob` (a queued claim is a promise the phone cannot keep),
// `setJobStatus` (the sync endpoint has no status op, so a queued one would be
// dropped hours later) and `confirmDayEnd` (both of those, plus a review held
// for six hours is a promise about a database that has moved on). All three
// refuse offline with a sentence.

import { post, put, isUnreachable, HttpError } from '../shared/http';
import { jobStatusPath, jobStatusBody, validateStatusChange, OFFLINE_REFUSAL } from '../shared/job/jobStatusActions';
import { DAY_END_CONFIRM_PATH } from './dayEnd';
import { getQueue, SYNC_PATH } from './offline/queue';
import { getPhotoQueue, defaultUpload, uploadPath } from './offline/photos';
import { splitOversized, mb, MAX_BYTES_PER_FILE } from '../shared/photoBatches';
import { isOnline } from './offline/useOffline';
import { uuid } from './offline/storage';
import { writeOptimisticNow, clearOptimisticNow } from './offline/optimisticNow';
import { geoFields, locate, fieldsFor } from './geo';
import { clockInGate } from './gpsGate';

export const CLAIM_PATH = '/field/claim';

export async function act(op, { queue, online, send, now = () => new Date() } = {}) {
  const q = queue || getQueue();
  const doSend = send || ((body) => post(SYNC_PATH, body));
  const isUp = online === undefined ? isOnline() : online;
  await q.ready;

  if (!isUp || q.pending().length > 0) {
    const queued = q.enqueue(op);
    if (isUp) q.replay(doSend).catch(() => {});
    return { sent: false, queued };
  }

  const record = { ...op, key: op.key || uuid(), at: op.at || now().toISOString() };
  let res;
  try {
    res = await doSend({ ops: [record] });
  } catch (e) {
    // `isUnreachable`, not `isOffline`: during a deploy the phone has signal
    // and nginx answers 502 because node is stopped. Treating that as a hard
    // failure made the crew member tap again afterwards, and the second tap
    // carries the later time — minutes off their paid day. The op keeps its
    // own `at`, so replaying it records when they actually stopped.
    if (!isUnreachable(e)) throw e;
    const queued = q.enqueue(record);
    return { sent: false, queued };
  }
  const result = res && Array.isArray(res.results) ? res.results[0] : null;
  if (!result) throw new HttpError(502, { error: 'The server did not answer that' }, SYNC_PATH);
  if (!result.ok) throw new HttpError(result.status || 400, { error: result.error || 'Could not be sent' }, SYNC_PATH);
  return { sent: true, result, record };
}

/** The LiveCard shape for a job we just clocked into, before the server confirms. */
export function optimisticNowFor(job, record) {
  return {
    entry_id: null,
    client_key: record.key,
    job_id: job.id,
    job_title: job.title,
    client_name: job.client_name || null,
    property_name: job.property_name || null,
    address: job.address || null,
    started_at: record.at,
    subtask_total: job.subtask_total || 0,
    subtask_done: job.subtask_done || 0,
    warning: null,
    queued: true,
  };
}

// The fix is taken WHEN THE BUTTON IS TAPPED, not when the queue drains, so a
// clock-in recorded in a yard and sent from the depot an hour later still
// carries the yard. locate() never throws and never waits more than a few
// seconds (geo.js, issue #128): no location must ever cost a shift.
//
// ── `gpsRequired`, and the one exception to the sentence above ──────────────
//
// Owner instruction, 2026-09-10: "a setting for admin/manager to toggle if
// worker is required to have gps on, then that worker can't start the clock if
// gps isn't on." That is the company's decision to make, and it is the ONLY
// thing in this app that may refuse a clock-in over a location. What keeps it
// from becoming #128 again is all in gpsGate.js — off unless set, satisfied by
// any fix however coarse, and every refusal names both the fix and a way out
// that does not need the phone to co-operate.
//
// Two properties of doing it HERE rather than in the screens:
//
//   * both screens that clock in (Today, Job) get the same answer. The `note`
//     defect is the standing lesson: it was fixed on Today on 2026-09-03 and
//     the job screen kept sending '' for another day.
//   * it is decided BEFORE the op exists, so a refusal never reaches the
//     offline queue. Geolocation needs no signal, so the rule behaves
//     identically with no bars — which is the only version that works, since a
//     queued clock-in that failed the rule would surface hours later in the
//     failed-items banner, long after the shift it was for.
//
// The caller passes the setting (`gpsRequired(settings)` from gpsGate.js,
// read from SettingsContext). It DEFAULTS TO FALSE so a screen that forgets,
// or a phone that never managed to fetch /api/settings, cannot block anybody.
//
// `closeNote`: why the crew member stopped the job they are on, when this
// clock-in switches them off it (screens/Job.js). It rides on THIS op as
// `close_note` rather than as a clock_out sent first, so the location rule
// above decides before anything is sent — a refusal leaves the first job
// running, never both stopped — and the offline queue replays it as one
// step. The server stores it on the closed entry and tells dispatch, as a
// clock-out with a reason does (routes/field.js applyOp).
export async function clockIn(job, opts = {}) {
  const { geo, gpsRequired = false, closeNote, ...rest } = opts;
  // Required overrides the person's own Record-location switch rather than
  // letting it block them: somebody who turned it off months ago must not
  // meet a new company policy as a clock-in that will not start. Me shows the
  // switch locked and says who locked it.
  const { fix, status } = await locate(gpsRequired ? { ...(geo || {}), enabled: true } : geo);
  const gps = clockInGate({ required: gpsRequired, status, fix });
  if (!gps.allow) {
    const refusal = new HttpError(0, { error: gps.message }, SYNC_PATH);
    refusal.gps = gps;
    throw refusal;
  }
  const switching = typeof closeNote === 'string' && closeNote.trim() ? { close_note: closeNote.trim() } : {};
  const out = await act({ op: 'clock_in', job_id: job.id, note: '', ...switching, ...fieldsFor(fix) }, rest);
  if (out.sent) clearOptimisticNow();
  else writeOptimisticNow({ key: out.queued.key, now: optimisticNowFor(job, out.queued) });
  // `gps` rides back so the screen can show a warning — no fix and the setting
  // off, or a fix so coarse it names a neighbourhood. Both are things only the
  // person holding the phone can do anything about.
  return { ...out, gps };
}

/**
 * Stop the clock, with the reason the crew member gave.
 *
 * `note` was hardcoded to '' until 2026-09-03, so every stop through the
 * rebuilt Field app recorded no reason at all — `closeEntry` writes
 * `pause_note = COALESCE(?, pause_note)` and an empty string became null — and
 * told dispatch nothing. The classic TimeTracker has always required a reason
 * and notified every admin and manager, so the cutover quietly stopped a piece
 * of information the office had been getting for years.
 *
 * The notification is the SERVER's job now (routes/field.js applyOp), because
 * this op can sit in the offline queue for hours: a notification fired from
 * the phone would either be lost or arrive before the stop it describes.
 */
export async function clockOut(now, opts = {}) {
  // `gpsRequired` is accepted and IGNORED, deliberately, so a screen can pass
  // the same options to both calls without a location policy ever standing
  // between somebody and the end of their shift. Stopping the clock is how a
  // person gets paid for hours they have already worked; there is no policy
  // under which they should be unable to. Asserted in actions.test.js.
  const { geo, note, gpsRequired: _ignored, ...rest } = opts;
  const where = await geoFields(geo);
  const op = {
    op: 'clock_out',
    entry_id: now && now.entry_id != null ? now.entry_id : null,
    entry_key: now && now.client_key ? now.client_key : null,
    note: typeof note === 'string' ? note.trim() : '',
    ...where,
  };
  const out = await act(op, rest);
  if (out.sent) clearOptimisticNow();
  else writeOptimisticNow({ key: out.queued.key, now: null });
  return out;
}

/**
 * Take a job nobody is on. The one Field write that does NOT queue.
 *
 * Every other op is safe to hold on the phone because it is about this
 * worker's own day — their clock, their notes, their photos. A claim is a
 * race against the rest of the crew, so a queued one is a promise the phone
 * cannot keep: two people offline both "claim" the same job, both see it as
 * theirs, and one of them finds out only after driving there. So we ask the
 * server and wait for the answer; with no signal we refuse and say why.
 *
 * Throws HttpError(409) with the server's sentence when somebody won the race.
 */
export async function claimJob(job, { online, send } = {}) {
  const isUp = online === undefined ? isOnline() : online;
  if (!isUp) {
    throw new HttpError(0, { error: 'You need a connection to claim a job. Nobody can hold it for you offline.' }, CLAIM_PATH);
  }
  const doSend = send || ((body) => post(CLAIM_PATH, body));
  try {
    return await doSend({ job_id: job.id });
  } catch (e) {
    if (isUnreachable(e)) {
      throw new HttpError(0, { error: 'The connection dropped before that went through. Check the list and try again.' }, CLAIM_PATH);
    }
    throw e;
  }
}

/**
 * Start, pause or finish a job. The SECOND Field write that does not queue.
 *
 * Not a choice about how important it is — the queue simply cannot carry it.
 * POST /api/field/sync replays through `applyOp`, whose switch knows
 * clock_in, clock_out, subtask, note and problem and answers 400
 * `Unknown op "status"` to anything else (server/routes/field.js). A queued
 * status change would be taken by the phone, held until signal, and then
 * dropped into the failed-items banner — with the crew member having believed
 * for hours that the job was marked done. Refusing up front is the honest
 * version of the same limitation, and it is what `claimJob` above does.
 *
 * The rules — who may, from which status, and whether a reason is required —
 * are apps/shared/job/jobStatusActions.js, which is checked against
 * server/routes/jobs.js by jobStatusContract.test.js. Nothing about them is
 * decided here.
 *
 * Throws HttpError(0) offline, HttpError(403) when the server refuses because
 * the caller is not the lead, and HttpError(400) with the server's own
 * sentence when a prerequisite job is not finished.
 */
export async function setJobStatus(job, action, note, { online, send } = {}) {
  const problem = validateStatusChange(action, note);
  if (problem) throw new HttpError(400, { error: problem }, jobStatusPath(job && job.id));
  const body = jobStatusBody(action, note);
  const path = jobStatusPath(job.id);

  const isUp = online === undefined ? isOnline() : online;
  if (!isUp) throw new HttpError(0, { error: OFFLINE_REFUSAL }, path);

  const doSend = send || ((p, b) => put(p, b));
  try {
    return await doSend(path, body);
  } catch (e) {
    if (isUnreachable(e)) throw new HttpError(0, { error: OFFLINE_REFUSAL }, path);
    throw e;
  }
}

/**
 * Confirm an end-of-day / end-of-week review. The THIRD Field write that does
 * not queue, for the same reason as the other two.
 *
 * POST /api/field/sync knows five ops and answers 400 `Unknown op` to anything
 * else, so a queued day-end would be taken by the phone, held until signal and
 * then dropped — with the crew member believing since Friday that their week
 * was closed. And a review is a set of promises about what the database looks
 * like RIGHT NOW; holding one for six hours and applying it then is precisely
 * the stale-plan case the signature exists to refuse.
 *
 * `signature` is the fingerprint of the plan that was on screen. The server
 * rebuilds the plan and compares: a mismatch is a 409 whose body carries the
 * FRESH plan, so the sheet redraws rather than blanking. That is not an error
 * to retry blindly — it is the review, again.
 *
 * Throws HttpError(0) offline and HttpError(409) with the server's sentence
 * when the plan moved under it.
 */
export async function confirmDayEnd(scope, signature, { online, send } = {}) {
  const isUp = online === undefined ? isOnline() : online;
  if (!isUp) {
    throw new HttpError(0, {
      error: 'Closing your jobs needs a connection — this one is not saved on the phone. Clocking in and out, ticks, notes and photos still work offline.',
    }, DAY_END_CONFIRM_PATH);
  }
  const doSend = send || ((body) => post(DAY_END_CONFIRM_PATH, body));
  try {
    return await doSend({ scope, signature });
  } catch (e) {
    if (isUnreachable(e)) {
      throw new HttpError(0, { error: 'The connection dropped before that went through. Open the review again and check.' }, DAY_END_CONFIRM_PATH);
    }
    throw e;
  }
}

export function toggleSubtask(subtaskId, completed, opts) {
  return act({ op: 'subtask', subtask_id: subtaskId, completed: !!completed }, opts);
}

export function addNote(jobId, text, opts) {
  return act({ op: 'note', job_id: jobId, text: String(text || '').trim() }, opts);
}

export function reportProblem({ job_id = null, text, severity = 'note' }, opts) {
  return act({ op: 'problem', job_id: job_id || null, text: String(text || '').trim(), severity }, opts);
}

/**
 * Photos follow the same rule as ops: upload now when online with nothing
 * waiting, otherwise keep the blob on the phone. No answer is a queue.
 *
 * ONE REQUEST PER PHOTO, deliberately, and it is not the thing that had a
 * limit. Tracing the "can't upload more than 10 at a time" report
 * (2026-09-13) through this loop: it never met media.js's per-request file
 * cap, because it never puts two photos in a request. That is worth keeping —
 * each photo carries its own `client_key`, which server/utils/clientOps.js
 * uses to make a retry idempotent, and each is independently queueable with no
 * signal. Batching would put twelve photos behind one key and one queue entry,
 * so a lost answer would re-send all twelve and a failure would fail all
 * twelve. The office path batches (../shared/photoBatches.js) because it has
 * neither of those properties to lose.
 *
 * ── What WAS wrong here ────────────────────────────────────────────────────
 *
 * `throw e` on a refusal, from inside the loop. Pick thirty photos, have the
 * third refused — a type the filter rejects, a job somebody was taken off —
 * and photos four to thirty were never uploaded, never queued, and never
 * mentioned. They existed only in a FileList the screen then cleared. The crew
 * member saw one red sentence about one photo and drove away.
 *
 * So a refusal no longer stops the others. It is collected, and:
 *
 *   • if anything got through, the outcome is RETURNED — `{ sent, queued,
 *     refused }` — so the screen can say "4 of 5 added" and name the one that
 *     was not. A partial upload reported as a success is the outcome this is
 *     all here to prevent.
 *   • if NOTHING got through, the first refusal is thrown, exactly as before.
 *     One photo refused must still read as one refusal with the server's own
 *     sentence, not as "0 of 1".
 *
 * A photo over the size multer will accept is refused HERE, before the bytes
 * are spent: `File.size` is already known, 40MB up a yard connection to be
 * told no is minutes of somebody's day, and with no signal it would otherwise
 * sit in the queue and fail hours later, after the crew has left the site and
 * cannot retake the shot smaller.
 */
export async function addPhotos(jobId, files, { photos, online, upload } = {}) {
  const pq = photos || getPhotoQueue();
  const doUpload = upload || defaultUpload;
  const isUp = online === undefined ? isOnline() : online;
  await pq.ready;
  let sent = 0;
  let queued = 0;
  const refused = [];
  const { sendable, oversized } = splitOversized(files);
  for (const file of oversized) {
    refused.push({
      name: file.name || 'that photo',
      error: new HttpError(400, {
        error: `Over ${mb(MAX_BYTES_PER_FILE)} — too large to upload.`,
      }, uploadPath(jobId)),
    });
  }
  for (const file of sendable) {
    const record = { key: uuid(), job_id: jobId, blob: file, caption: '', name: file.name || '' };
    if (!isUp || pq.pending().length > 0) { pq.enqueue(record); queued++; continue; }
    try {
      await doUpload(record);
      sent++;
    } catch (e) {
      if (isUnreachable(e)) { pq.enqueue(record); queued++; continue; }
      refused.push({ name: record.name || 'that photo', error: e });
    }
  }
  if (queued > 0 && isUp) pq.replay(doUpload).catch(() => {});
  // Nothing survived: this is a refusal, not a partial result.
  if (sent === 0 && queued === 0 && refused.length) throw refused[0].error;
  return { sent, queued, refused };
}
